import React, { useState, useRef, useEffect } from 'react';
import { 
  Heart, MessageCircle, MoreHorizontal, 
  Image as ImageIcon, Send, Flag, X, 
  Plus, Edit2, Trash2, Download, 
  Clock, Globe, Lock, Hourglass, Camera, Video, 
  Users, RefreshCw, Play, Loader2, Calendar, MapPin, CheckSquare, Square, ChevronDown
} from 'lucide-react';
import { Post, FamilyMember } from '../types';
import { collection, addDoc, updateDoc, doc, setDoc, deleteDoc, getDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../services/firebaseConfig';
import { authService } from '../services/authService';
import QuestBoard from './QuestBoard';

// --- TYPES ---
interface Comment {
  id: string;
  authorId: string;
  text: string;
  timestamp: string;
  replies: Comment[];
}

interface SocialFeedProps {
  posts: Post[];
  members: Record<string, FamilyMember>;
  currentUser: FamilyMember;
  onLocateInTree: (memberId: string) => void;
  onUpdatePosts: (posts: Post[]) => void;
  onLikePost: (postId: string) => void;
  onDeletePost: (postId: string) => void;
  getUserGrandparents?: () => string[];
  getBranchMembers?: (grandparentId: string) => string[];
  getBranchPosts?: (grandparentId: string) => Post[];
}

// --- HELPER: Smart Compression (Blob for Storage) ---
const compressMedia = (file: File): Promise<Blob | File> => {
  return new Promise((resolve, reject) => {
    if (file.type.startsWith('video/')) {
      resolve(file); // No client-side compression for video
      return;
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const maxWidth = 1280;
        const scale = maxWidth / img.width;
        canvas.width = maxWidth;
        canvas.height = img.height * scale;
        ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
            if (blob) resolve(blob);
            else reject(new Error('Compression failed'));
        }, 'image/jpeg', 0.8);
      };
      img.onerror = (err) => reject(err);
    };
    reader.onerror = (err) => reject(err);
  });
};

// --- HELPER: Time Ago ---
const timeAgo = (dateString: string) => {
  const diff = Date.now() - new Date(dateString).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
};

// --- COMPONENT: Member Selector Modal ---
const MemberSelectorModal = ({ members, selectedIds, onToggle, onClose }: any) => (
  <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 backdrop-blur-sm" onClick={onClose}>
    <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden flex flex-col max-h-[80vh]" onClick={e => e.stopPropagation()}>
      <div className="p-4 border-b border-gray-100 flex justify-between items-center">
        <h3 className="font-bold text-gray-900">Select Audience</h3>
        <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
      </div>
      <div className="overflow-y-auto p-2">
        {Object.values(members).map((m: any) => (
          <div key={m.id} onClick={() => onToggle(m.id)} className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${selectedIds.includes(m.id) ? 'bg-indigo-50 border border-indigo-100' : 'hover:bg-gray-50 border border-transparent'}`}>
            <div className={`w-5 h-5 rounded-md border flex items-center justify-center ${selectedIds.includes(m.id) ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300 bg-white'}`}>
              {selectedIds.includes(m.id) && <CheckSquare className="w-3.5 h-3.5 text-white" />}
            </div>
            <img src={m.avatarUrl} className="w-8 h-8 rounded-full bg-gray-200" />
            <span className="text-sm font-bold text-gray-700">{m.name}</span>
          </div>
        ))}
      </div>
      <div className="p-3 border-t border-gray-100">
        <button onClick={onClose} className="w-full bg-indigo-600 text-white font-bold py-2.5 rounded-xl">Done</button>
      </div>
    </div>
  </div>
);

// --- COMPONENT: Story Viewer ---
const StoryViewer = ({ stories, initialIndex, onClose }: { stories: any[], initialIndex: number, onClose: () => void }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  
  const activeUser = stories[currentIndex];
  // @ts-ignore
  const storyMedia = activeUser?.storyUrl;
  // @ts-ignore
  const isVideo = activeUser?.storyType === 'video';

  useEffect(() => {
    setProgress(0);
  }, [currentIndex]);

  useEffect(() => {
    if (isPaused) return;
    const duration = isVideo ? 15000 : 5000; 
    const interval = 100;
    const step = 100 / (duration / interval);

    const timer = setInterval(() => {
      setProgress(p => {
        if (p >= 100) {
          if (currentIndex < stories.length - 1) {
             setCurrentIndex(c => c + 1);
             return 0;
          } else {
             onClose();
             return 100;
          }
        }
        return p + step;
      });
    }, interval);

    return () => clearInterval(timer);
  }, [currentIndex, isPaused, isVideo, stories.length, onClose]);

  if (!activeUser) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black flex items-center justify-center sm:p-4 animate-fade-in">
        <div className="absolute top-4 left-4 right-4 flex gap-1 z-20 pointer-events-none">
            {stories.map((_, idx) => (
                <div key={idx} className="h-1 flex-1 bg-white/30 rounded-full overflow-hidden">
                    <div className={`h-full bg-white transition-all ease-linear ${idx === currentIndex ? 'duration-100' : 'duration-0'}`} style={{ width: idx < currentIndex ? '100%' : idx === currentIndex ? `${progress}%` : '0%' }} />
                </div>
            ))}
        </div>
        <button onClick={onClose} className="absolute top-8 right-4 text-white p-2 z-50 hover:scale-110 transition-transform bg-black/20 rounded-full backdrop-blur-sm pointer-events-auto"><X className="w-6 h-6 drop-shadow-lg" /></button>
        {/* Navigation Areas */}
        <div className="absolute inset-y-0 left-0 w-1/3 z-10" onClick={(e) => { e.stopPropagation(); setCurrentIndex(c => Math.max(0, c - 1)); }}></div>
        <div className="absolute inset-y-0 right-0 w-1/3 z-10" onClick={(e) => { e.stopPropagation(); setCurrentIndex(c => Math.min(stories.length - 1, c + 1)); }}></div>
        <div
            className="relative w-full md:max-w-md h-full md:h-[90vh] bg-gray-900 md:rounded-3xl overflow-hidden flex flex-col shadow-2xl pointer-events-none"
            onMouseDown={() => setIsPaused(true)}
            onMouseUp={() => setIsPaused(false)}
            onTouchStart={() => setIsPaused(true)}
            onTouchEnd={() => setIsPaused(false)}
            onTouchCancel={() => setIsPaused(false)}
        >
            <div className="absolute top-6 left-4 flex items-center gap-3 z-10">
                <img src={activeUser.avatarUrl || `https://ui-avatars.com/api/?name=${activeUser.name}`} className="w-10 h-10 rounded-full border-2 border-indigo-500 shadow-md" />
                <div className="flex flex-col"><span className="text-white font-bold drop-shadow-md text-sm">{activeUser.name}</span><span className="text-gray-300 text-[10px] drop-shadow-md flex items-center gap-1"><Clock className="w-3 h-3" /> 24h</span></div>
            </div>
            <div className="flex-1 bg-black flex items-center justify-center relative">
                {storyMedia ? (
                    isVideo ? <video src={storyMedia} autoPlay loop muted={false} className="w-full h-full object-cover" /> : <img src={storyMedia} className="w-full h-full object-cover" />
                ) : (
                    <div className="text-center px-8 pointer-events-none">
                        <p className="text-white text-xl font-medium">Content unavailable</p>
                    </div>
                )}
            </div>
        </div>
    </div>
  );
};

// --- COMPONENT: Lightbox (Images & Videos) ---
const ImageLightbox = ({ src, type, onClose }: { src: string, type?: 'image' | 'video', onClose: () => void }) => (
  <div className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center p-4 animate-fade-in backdrop-blur-sm" onClick={onClose}>
    <button className="absolute top-4 right-4 text-white p-3 hover:bg-white/10 rounded-full transition-colors"><X className="w-8 h-8" /></button>
    <div className="max-w-5xl w-full flex flex-col items-center" onClick={e => e.stopPropagation()}>
        {type === 'video' ? (
            <video src={src} controls autoPlay className="max-w-full max-h-[85vh] rounded-lg shadow-2xl bg-black" />
        ) : (
            <img src={src} className="max-w-full max-h-[85vh] rounded-lg shadow-2xl transform transition-transform" />
        )}
        <div className="flex gap-4 mt-6">
            <a href={src} download target="_blank" rel="noreferrer" className="flex items-center gap-2 px-6 py-3 bg-white/10 text-white rounded-full hover:bg-white/20 backdrop-blur-md transition-all font-bold border border-white/10">
                <Download className="w-5 h-5" /> Download
            </a>
        </div>
    </div>
  </div>
);

// --- COMPONENT: Comment Item (Recursive) ---
const CommentItem = ({ comment, members, depth = 0, onReply }: { comment: Comment, members: any, depth?: number, onReply: (id: string, name: string) => void }) => {
  const author = members[comment.authorId] || { name: 'Unknown', avatarUrl: '' };
  return (
    <div className={`mt-3 ${depth > 0 ? 'ml-8 pl-3 border-l-2 border-gray-100' : ''}`}>
      <div className="flex gap-3 items-start">
        <img src={author.avatarUrl || `https://ui-avatars.com/api/?name=${author.name}`} className="w-8 h-8 rounded-full mt-1 border border-gray-100" />
        <div className="flex-1">
            <div className="bg-gray-50 p-3 rounded-2xl rounded-tl-none inline-block">
                <span className="text-xs font-black text-gray-900 block mb-0.5">{author.name}</span>
                <p className="text-xs text-gray-700 leading-relaxed">{comment.text}</p>
            </div>
            <div className="flex gap-4 mt-1.5 ml-2 text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                <span>{timeAgo(comment.timestamp)}</span>
                <button className="hover:text-pink-600 transition-colors">Like</button>
                <button onClick={() => onReply(comment.id, author.name)} className="hover:text-indigo-600 transition-colors">Reply</button>
            </div>
        </div>
      </div>
      {/* Recursion */}
      {comment.replies?.map(r => <CommentItem key={r.id} comment={r} members={members} depth={depth + 1} onReply={onReply} />)}
    </div>
  );
};
// --- COMPONENT: Likes Modal ---
const LikesModal = ({ likeIds, members, onClose }: { likeIds: string[], members: Record<string, FamilyMember>, onClose: () => void }) => (
  <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 backdrop-blur-sm" onClick={onClose}>
    <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden flex flex-col max-h-[80vh]" onClick={e => e.stopPropagation()}>
      <div className="p-4 border-b border-gray-100 flex justify-between items-center">
        <h3 className="font-bold text-gray-900 flex items-center gap-2"><Heart className="w-5 h-5 text-pink-600 fill-pink-600" /> {likeIds.length} {likeIds.length === 1 ? 'Like' : 'Likes'}</h3>
        <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
      </div>
      <div className="overflow-y-auto p-2">
        {likeIds.length > 0 ? (
          likeIds.map(memberId => {
            const member = members[memberId];
            if (!member) return null;
            return (
              <div key={memberId} className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors border border-transparent">
                <img src={member.avatarUrl} className="w-10 h-10 rounded-full bg-gray-200" />
                <span className="text-sm font-bold text-gray-700">{member.name}</span>
              </div>
            );
          })
        ) : (
          <div className="text-center py-8 text-gray-400">
            <Heart className="w-8 h-8 mx-auto mb-2 opacity-20" />
            <p className="text-xs">No likes yet</p>
          </div>
        )}
      </div>
      <div className="p-3 border-t border-gray-100">
        <button onClick={onClose} className="w-full bg-indigo-600 text-white font-bold py-2.5 rounded-xl">Close</button>
      </div>
    </div>
  </div>
);

// --- COMPONENT: Edit Post Modal ---
const EditPostModal = ({ post, onClose, onSave }: { post: Post, onClose: () => void, onSave: (id: string, newContent: string) => void }) => {
  const [content, setContent] = useState(post.content);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl p-6 animate-scale-in border border-gray-100">
        <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Edit2 className="w-5 h-5 text-indigo-600" /> Edit Post</h3>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full transition-colors"><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <textarea className="w-full h-40 p-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none resize-none text-sm transition-all" value={content} onChange={(e) => setContent(e.target.value)} />
        <div className="flex justify-end gap-3 mt-4">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 font-bold hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
          <button onClick={() => onSave(post.id, content)} className="px-6 py-2 bg-indigo-600 text-white font-bold rounded-lg hover:bg-indigo-700 shadow-lg shadow-indigo-200 transition-all">Save Changes</button>
        </div>
      </div>
    </div>
  );
};

// --- MAIN COMPONENT: SOCIAL FEED ---
const SocialFeed: React.FC<SocialFeedProps> = ({ posts, members, currentUser, onLocateInTree, onUpdatePosts, onDeletePost, getUserGrandparents, getBranchMembers, getBranchPosts }) => {
  // All hooks must be called before any early returns
  // Branch Filter State
  const [selectedGrandparent, setSelectedGrandparent] = useState<string | null>(null);
  
  // Initialize selected grandparent on mount
  useEffect(() => {
    if (getUserGrandparents && !selectedGrandparent) {
      const grandparents = getUserGrandparents();
      if (grandparents.length > 0) {
        setSelectedGrandparent(grandparents[0]);
      }
    }
  }, [getUserGrandparents, selectedGrandparent]);

  // Post Creation
  const [mode, setMode] = useState<'status' | 'event'>('status');
  const [content, setContent] = useState('');
  const [eventDetails, setEventDetails] = useState({ title: '', date: '', location: '' });
  
  // Media Upload
  const [selectedFile, setSelectedFile] = useState<Blob | File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fileType, setFileType] = useState<'image' | 'video'>('image');
  const [isUploading, setIsUploading] = useState(false);
  
  // Options
  const [isTimeCapsule, setIsTimeCapsule] = useState(false);
  const [unlockDate, setUnlockDate] = useState('');
  const [targetAudience, setTargetAudience] = useState<'public' | 'specific'>('public');
  const [selectedAudienceIds, setSelectedAudienceIds] = useState<string[]>([]);
  const [showAudienceModal, setShowAudienceModal] = useState(false);

  // Interaction
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [activeCommentId, setActiveCommentId] = useState<string | null>(null);
  const [commentText, setCommentText] = useState('');
  const [replyTarget, setReplyTarget] = useState<{id: string, name: string} | null>(null);
  
  // Media Upload Menu
  const [mediaMenuOpen, setMediaMenuOpen] = useState<'gallery' | 'camera' | null>(null);
  
  // Modals
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [lightboxImage, setLightboxImage] = useState<{src: string, type: 'image'|'video'} | null>(null);
  const [viewingStoryIndex, setViewingStoryIndex] = useState<number | null>(null);
  const [viewingLikesPostId, setViewingLikesPostId] = useState<string | null>(null);

  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryVideoInputRef = useRef<HTMLInputElement>(null);
  const selfiePhotoInputRef = useRef<HTMLInputElement>(null);
  const selfieVideoInputRef = useRef<HTMLInputElement>(null);
  const storyInputRef = useRef<HTMLInputElement>(null);

  // Safety check - if currentUser or members are missing, show loading state
  if (!currentUser || !members || Object.keys(members).length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full bg-indigo-100 mx-auto mb-4 flex items-center justify-center">
            <div className="w-8 h-8 rounded-full border-2 border-indigo-300 border-t-indigo-600 animate-spin"></div>
          </div>
          <p className="text-slate-600 font-bold">Loading feed...</p>
        </div>
      </div>
    );
  }

  // --- ACTIONS ---

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
        const file = e.target.files[0];
        setFileType(file.type.startsWith('video/') ? 'video' : 'image');
        setPreviewUrl(URL.createObjectURL(file));
        // Only compress if image, videos pass through (handled by security rules size limit)
        const processed = await compressMedia(file);
        setSelectedFile(processed);
    }
  };

  const handleStoryUpload = async (e: React.ChangeEvent<HTMLInputElement>, source: 'gallery' | 'selfie-photo' | 'selfie-video' = 'gallery') => {
    const file = e.target.files?.[0];
    if (file) {
        try {
            const isVideo = file.type.startsWith('video/');
            const ext = isVideo ? 'mp4' : 'jpg';
            const storageRef = ref(storage, `stories/${source}/${currentUser.id}/${Date.now()}.${ext}`);
            
            // Upload with compression for images
            let uploadFile: Blob | File = file;
            if (!isVideo) {
                uploadFile = await compressMedia(file);
            }
            
            const snapshot = await uploadBytes(storageRef, uploadFile);
            const url = await getDownloadURL(snapshot.ref);

            // Update MEMBER profile in Firestore with story data
            const treeRef = doc(db, 'family-data', 'main-tree');
            await updateDoc(treeRef, {
                [`members.${currentUser.id}.storyUrl`]: url,
                [`members.${currentUser.id}.storyType`]: isVideo ? 'video' : 'image',
                [`members.${currentUser.id}.storyTimestamp`]: new Date().toISOString()
            });

            alert(`Story uploaded from ${source === 'gallery' ? 'gallery' : source === 'selfie-photo' ? 'selfie' : 'video'}!`);
            
            // Reset input
            e.target.value = '';
        } catch (err) {
            console.error(err);
            alert("Story upload failed. Check your connection.");
        }
    }
  };

  const handlePostSubmit = async () => {
    if (!content.trim() && !selectedFile && mode === 'status') return;
    if (mode === 'event' && (!eventDetails.title || !eventDetails.date)) return alert("Event details required");

    setIsUploading(true);
    try {
        let downloadUrl = null;
        if (selectedFile) {
            const folder = fileType === 'video' ? 'posts_videos' : 'posts';
            const ext = fileType === 'video' ? 'mp4' : 'jpg';
            const storageRef = ref(storage, `${folder}/${Date.now()}.${ext}`);
            const snapshot = await uploadBytes(storageRef, selectedFile);
            downloadUrl = await getDownloadURL(snapshot.ref);
        }

        const newPost: any = {
            id: `post-${Date.now()}`,
            authorId: currentUser.id,
            firebaseUid: authService.getCurrentUser()?.uid,
            familyId: currentUser.familyId,
            content: content,
            imageUrl: downloadUrl,
            mediaType: fileType,
            timestamp: new Date().toISOString(),
            type: isTimeCapsule ? 'capsule' : mode, // 'status', 'event', 'capsule'
            privacy: targetAudience,
            targetAudienceIds: targetAudience === 'specific' ? selectedAudienceIds : [],
            likes: [],
            comments: 0,
            commentsList: [],
            // Event Specifics (Invite System)
            eventDetails: mode === 'event' ? eventDetails : null,
            rsvp: mode === 'event' ? { attending: [], maybe: [], declined: [] } : null
        };

        if (isTimeCapsule && unlockDate) {
            newPost.unlockDate = unlockDate;
            newPost.isLocked = true;
        }

        await setDoc(doc(db, "posts", newPost.id), newPost);
        
        // Update Local State Optimistically
        onUpdatePosts([newPost, ...posts]);
        
        // Cleanup
        setContent(''); setSelectedFile(null); setPreviewUrl(null); 
        setMode('status'); setEventDetails({title:'', date:'', location:''});
        setTargetAudience('public'); setSelectedAudienceIds([]); setIsTimeCapsule(false);
    } catch (e) {
        console.error(e);
        alert("Post failed.");
    } finally {
        setIsUploading(false);
    }
  };

  const handleRSVP = async (postId: string, status: 'attending' | 'maybe' | 'declined') => {
      const post = posts.find(p => p.id === postId);
      if (!post || !post.rsvp) return;

      const newRsvp = {
          attending: post.rsvp.attending.filter((id: string) => id !== currentUser.id),
          maybe: post.rsvp.maybe.filter((id: string) => id !== currentUser.id),
          declined: post.rsvp.declined.filter((id: string) => id !== currentUser.id),
      };
      // @ts-ignore
      newRsvp[status].push(currentUser.id);

      // Local Update
      const updatedPosts = posts.map(p => p.id === postId ? { ...p, rsvp: newRsvp } : p);
      onUpdatePosts(updatedPosts);

      // DB Update
      const postRef = doc(db, "posts", postId);
      await updateDoc(postRef, { rsvp: newRsvp });
  };

  const handleRemind = async (postId: string) => {
      const postRef = doc(db, "posts", postId);
      // Update timestamp to now to "bump" it
      await updateDoc(postRef, { timestamp: new Date().toISOString() });
      alert("Post bumped to top for all viewers.");
      // Note: Real-time listener in App.tsx will catch this and re-order the feed.
  };

  // Helper to add nested comment
  const insertComment = (list: Comment[], targetId: string | null, newComment: Comment): Comment[] => {
      if (!targetId) return [...list, newComment]; // Top level
      return list.map(c => {
          if (c.id === targetId) return { ...c, replies: [...(c.replies || []), newComment] };
          if (c.replies) return { ...c, replies: insertComment(c.replies, targetId, newComment) };
          return c;
      });
  };

  const handleComment = async (postId: string) => {
      if (!commentText.trim()) return;
      const newComment: Comment = {
          id: `c-${Date.now()}`, authorId: currentUser.id, text: commentText, timestamp: new Date().toISOString(), replies: []
      };
      
      const post = posts.find(p => p.id === postId);
      if (!post) return;

      // @ts-ignore
      const oldList = post.commentsList || [];
      const newList = insertComment(oldList, replyTarget?.id || null, newComment);

      // Optimistic
      const updatedPosts = posts.map(p => p.id === postId ? { ...p, commentsList: newList, comments: (p.comments || 0) + 1 } : p);
      onUpdatePosts(updatedPosts);

      // DB Update
      const postRef = doc(db, "posts", postId);
      await updateDoc(postRef, { commentsList: newList, comments: (post.comments || 0) + 1 });
      
      setCommentText(''); setReplyTarget(null);
  };

  const handleEditSave = async (postId: string, newContent: string) => {
      const post = posts.find(p => p.id === postId);
      if (!post) return;

      // Optimistic update
      const updatedPosts = posts.map(p => p.id === postId ? { ...p, content: newContent } : p);
      onUpdatePosts(updatedPosts);

      // DB Update
      try {
          const postRef = doc(db, "posts", postId);
          await updateDoc(postRef, { content: newContent });
          setEditingPost(null);
      } catch (e) {
          console.error(e);
          alert("Failed to update post.");
      }
  };

  const handleDeletePost = async (postId: string) => {
      if (!window.confirm("Delete this post?")) return;
      try { await deleteDoc(doc(db, "posts", postId)); onDeletePost(postId); } catch (e) { alert("Error deleting."); }
  };

  const handleLocalLike = async (postId: string) => {
      const post = posts.find(p => p.id === postId);
      if (!post) return;

      // Toggle like - remove if already liked, add if not
      const newLikes = post.likes.includes(currentUser.id)
          ? post.likes.filter(id => id !== currentUser.id)
          : [...post.likes, currentUser.id];

      // Optimistic update
      const updatedPosts = posts.map(p => p.id === postId ? { ...p, likes: newLikes } : p);
      onUpdatePosts(updatedPosts);

      // DB Update
      try {
          const postRef = doc(db, "posts", postId);
          await updateDoc(postRef, { likes: newLikes });
      } catch (e) {
          console.error(e);
          alert("Failed to update likes.");
      }
  };

  const handleReport = async (post: any) => {
    try {
      const reportId = `report-${Date.now()}`;
      const complaint = {
        id: reportId,
        postId: post.id,
        reporterName: currentUser.name || 'Anonymous',
        reporterId: currentUser.id,
        reason: 'Inappropriate Content',
        severity: 'High',
        status: 'Pending',
        timestamp: new Date().toISOString(),
        contentPreview: post.content?.substring(0, 100),
        imagePreview: post.imageUrl || '',
        authorId: post.authorId,
        authorName: members[post.authorId]?.name || 'Unknown',
        familyId: currentUser.familyId
      };
      
      await setDoc(doc(db, "complaints", reportId), complaint);
      alert("✅ Report submitted. Admin team will review it shortly.");
    } catch (error) {
      console.error("Error submitting report:", error);
      alert("Failed to submit report. Please try again.");
    }
  };

  // Filter stories: exclude self, only show if storyUrl exists
  const storiesList = Object.values(members).filter(m => m.id !== currentUser.id && (m as any).storyUrl).slice(0, 10);

  return (
    <div className="max-w-2xl mx-auto pb-32 px-4 md:px-0 w-full overflow-x-hidden">
      
      <QuestBoard members={members} onSolve={(type, id) => onLocateInTree(id === 'invite' ? 'invite' : id)} currentUser={currentUser.id} />
      
      {showAudienceModal && (
          <MemberSelectorModal 
            members={members} selectedIds={selectedAudienceIds} 
            onToggle={(id) => setSelectedAudienceIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id])}
            onClose={() => setShowAudienceModal(false)}
          />
      )}

      {/* 1. STORIES (Fixed Upload) */}
      {/* ...existing code... */}
      
      {/* Overlays */}
      {viewingStoryIndex !== null && <StoryViewer stories={storiesList} initialIndex={viewingStoryIndex} onClose={() => setViewingStoryIndex(null)} />}
      {editingPost && <EditPostModal post={editingPost} onClose={() => setEditingPost(null)} onSave={handleEditSave} />}
      {lightboxImage && <ImageLightbox src={lightboxImage.src} type={lightboxImage.type} onClose={() => setLightboxImage(null)} />}
      {viewingLikesPostId && (
          <LikesModal 
            likeIds={posts.find(p => p.id === viewingLikesPostId)?.likes || []} 
            members={members} 
            onClose={() => setViewingLikesPostId(null)} 
          />
      )}

      {/* 2. POST CREATOR (Tabs for Status / Event) */}
      <div className="bg-white p-3 rounded-2xl shadow-sm border border-gray-100 mb-6">
         <div className="flex gap-2 mb-3 border-b border-gray-100 pb-2">
            <button onClick={() => setMode('status')} className={`text-xs font-bold px-2 py-1 rounded-full transition-colors ${mode === 'status' ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-50'}`}>Post Update</button>
            <button onClick={() => setMode('event')} className={`text-xs font-bold px-2 py-1 rounded-full transition-colors ${mode === 'event' ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:bg-gray-50'}`}>Create Invite</button>
         </div>

         <div className="flex gap-3">
            <img src={currentUser.avatarUrl} className="w-10 h-10 rounded-full bg-gray-200 border border-gray-100" />
            <div className="flex-1 space-y-3">
                {mode === 'status' ? (
                   <textarea value={content} onChange={e => setContent(e.target.value)} placeholder={`What's on your mind, ${currentUser.name.split(' ')[0]}?`} className="w-full bg-transparent text-sm outline-none resize-none h-16 placeholder:text-gray-400" />
                ) : (
                   <div className="space-y-1 bg-gray-50 p-1.5 rounded-xl">
                      <input value={eventDetails.title} onChange={e => setEventDetails({...eventDetails, title: e.target.value})} placeholder="Event Title (e.g., Birthday Bash)" className="w-full bg-white p-1 rounded-lg text-xs font-bold outline-none border border-gray-200" />
                      <div className="flex gap-1">
                         <input type="date" value={eventDetails.date} onChange={e => setEventDetails({...eventDetails, date: e.target.value})} className="flex-1 bg-white p-1 rounded-lg text-xs font-bold outline-none border border-gray-200" />
                         <input value={eventDetails.location} onChange={e => setEventDetails({...eventDetails, location: e.target.value})} placeholder="Location" className="flex-1 bg-white p-1 rounded-lg text-xs outline-none border border-gray-200" />
                      </div>
                      <textarea value={content} onChange={e => setContent(e.target.value)} placeholder="Add details..." className="w-full bg-transparent text-xs outline-none resize-none h-6 px-1" />
                   </div>
                )}
                
                {/* PREVIEW */}
                {previewUrl && (
                    <div className="relative rounded-xl overflow-hidden bg-black h-48">
                        {fileType === 'video' ? <video src={previewUrl} controls className="h-full w-full object-contain" /> : <img src={previewUrl} className="h-full w-full object-cover" />}
                        <button onClick={() => {setPreviewUrl(null); setSelectedFile(null)}} className="absolute top-2 right-2 bg-black/60 text-white p-1.5 rounded-full hover:bg-black/80"><X className="w-4 h-4" /></button>
                    </div>
                )}
                
                {/* CAPSULE UI */}
                {isTimeCapsule && mode === 'status' && (
                    <div className="flex items-center gap-2 bg-amber-50 p-2 rounded-lg border border-amber-100">
                        <Hourglass className="w-4 h-4 text-amber-600" />
                        <span className="text-[10px] font-bold text-amber-800">Unlock on:</span>
                        <input type="date" value={unlockDate} onChange={e => setUnlockDate(e.target.value)} className="bg-transparent text-xs font-bold text-amber-900 outline-none" />
                    </div>
                )}

                {/* TOOLBAR (Mobile Optimized Layout) */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pt-2 border-t border-gray-50 gap-2">
                    {/* CHANGED: Used 'flex-wrap' and 'z-20' so dropdowns are visible and not cut off */}
                    <div className="flex gap-2 flex-wrap pb-1 relative z-20">
                        {/* 1. File Inputs (Hidden) - Configured for specific actions */}
                        <input type="file" ref={fileInputRef} hidden accept="image/*" onChange={handleFileSelect} />
                        <input type="file" ref={galleryVideoInputRef} hidden accept="video/*" onChange={handleFileSelect} />
                        <input type="file" ref={cameraInputRef} hidden accept="image/*" capture="environment" onChange={handleFileSelect} />
                        <input type="file" ref={selfieVideoInputRef} hidden accept="video/*" capture="user" onChange={handleFileSelect} />
                        
                        {/* 2. Gallery Button with Dropdown */}
                        <div className="relative">
                            <button 
                                onClick={() => setMediaMenuOpen(mediaMenuOpen === 'gallery' ? null : 'gallery')} 
                                className={`p-2 rounded-full transition-colors ${mediaMenuOpen === 'gallery' ? 'bg-indigo-100 text-indigo-600' : 'bg-gray-50 hover:bg-gray-100 text-indigo-500'}`}
                                title="Gallery"
                            >
                                <ImageIcon className="w-5 h-5" />
                            </button>
                            
                            {mediaMenuOpen === 'gallery' && (
                                <div className="absolute bottom-12 left-0 bg-white shadow-xl border border-gray-200 rounded-xl overflow-hidden w-48 z-50 animate-scale-in">
                                    <button onClick={() => { fileInputRef.current?.click(); setMediaMenuOpen(null); }} className="w-full text-left px-4 py-3 text-xs font-bold text-gray-700 hover:bg-gray-50 flex items-center gap-2 border-b border-gray-100">
                                        <ImageIcon className="w-4 h-4 text-indigo-500" /> Photo from Gallery
                                    </button>
                                    <button onClick={() => { galleryVideoInputRef.current?.click(); setMediaMenuOpen(null); }} className="w-full text-left px-4 py-3 text-xs font-bold text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                                        <Video className="w-4 h-4 text-purple-500" /> Video from Gallery
                                    </button>
                                </div>
                            )}
                        </div>
                        
                        {/* 3. Camera Button with Dropdown */}
                        <div className="relative">
                            <button 
                                onClick={() => setMediaMenuOpen(mediaMenuOpen === 'camera' ? null : 'camera')} 
                                className={`p-2 rounded-full transition-colors ${mediaMenuOpen === 'camera' ? 'bg-blue-100 text-blue-600' : 'bg-gray-50 hover:bg-gray-100 text-blue-500'}`}
                                title="Camera"
                            >
                                <Camera className="w-5 h-5" />
                            </button>
                            
                            {mediaMenuOpen === 'camera' && (
                                <div className="absolute bottom-12 left-0 bg-white shadow-xl border border-gray-200 rounded-xl overflow-hidden w-48 z-50 animate-scale-in">
                                    <button onClick={() => { cameraInputRef.current?.click(); setMediaMenuOpen(null); }} className="w-full text-left px-4 py-3 text-xs font-bold text-gray-700 hover:bg-gray-50 flex items-center gap-2 border-b border-gray-100">
                                        <Camera className="w-4 h-4 text-blue-500" /> Selfie Photo
                                    </button>
                                    <button onClick={() => { selfieVideoInputRef.current?.click(); setMediaMenuOpen(null); }} className="w-full text-left px-4 py-3 text-xs font-bold text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                                        <Video className="w-4 h-4 text-blue-600" /> Selfie Video
                                    </button>
                                </div>
                            )}
                        </div>
                        
                        {/* 4. Other Buttons */}
                        {mode === 'status' && (
                            <button onClick={() => setIsTimeCapsule(!isTimeCapsule)} className={`p-2 rounded-full transition-colors ${isTimeCapsule ? 'bg-amber-100 text-amber-600' : 'bg-gray-50 text-gray-400 hover:bg-gray-100'}`} title="Time Capsule">
                                <Lock className="w-5 h-5" />
                            </button>
                        )}
                        
                        <button onClick={() => { if(targetAudience === 'public') setShowAudienceModal(true); setTargetAudience(p => p === 'public' ? 'specific' : 'public') }} className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[10px] font-bold transition-colors whitespace-nowrap ${targetAudience === 'specific' ? 'bg-purple-100 text-purple-700' : 'bg-gray-50 text-gray-500'}`}>
                           <Users className="w-3 h-3" /> {targetAudience === 'public' ? 'Public' : 'Specific'}
                        </button>
                    </div>
                    <button onClick={handlePostSubmit} disabled={isUploading} className="px-3 py-2 bg-indigo-600 text-white rounded-xl text-xs font-bold hover:bg-indigo-700 flex items-center justify-center gap-1.5 transition-all shadow-md active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap">
                       {isUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} {mode === 'event' ? 'Invite' : 'Post'}
                    </button>
                </div>
            </div>
         </div>
      </div>

      {/* BRANCH FILTER SELECTOR - DISABLED - Show all family posts */}
      {/* {getUserGrandparents && getBranchPosts && (
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-100 px-6 py-4 sticky top-0 z-20">
          <div className="max-w-2xl mx-auto flex items-center gap-4">
            <span className="text-xs font-bold text-gray-600 uppercase tracking-wider">View Branch:</span>
            <select
              value={selectedGrandparent || ''}
              onChange={(e) => setSelectedGrandparent(e.target.value)}
              className="px-4 py-2 bg-white border border-indigo-200 rounded-lg text-sm font-bold text-gray-900 focus:ring-2 focus:ring-indigo-500 outline-none cursor-pointer"
            >
              {getUserGrandparents().map((grandparentId) => {
                const grandparent = members[grandparentId];
                return (
                  <option key={grandparentId} value={grandparentId}>
                    👨‍👴 {grandparent?.name || 'Unknown Grandparent'}
                  </option>
                );
              })}
            </select>
            <div className="text-xs text-gray-600 ml-auto">
              <span className="font-bold text-indigo-600">
                {selectedGrandparent && getBranchPosts ? getBranchPosts(selectedGrandparent).length : 0}
              </span> posts from this branch
            </div>
          </div>
        </div>
      )} */}

      {/* 3. FEED STREAM */}
      <div className="space-y-6">
         {posts.map(post => {
             // PRIVACY FILTER: Strictly hide unless Author, Public, or Targeted
             if (post.privacy === 'specific' && post.authorId !== currentUser.id && !post.targetAudienceIds?.includes(currentUser.id)) return null;

             const author = post.isSystemBroadcast 
               ? { name: 'Famitry Admin', avatarUrl: '', id: 'system-official' }
               : (members[post.authorId] || { name: 'Unknown', avatarUrl: '' });
             // @ts-ignore
             const isLocked = post.type === 'capsule' && new Date(post.unlockDate) > new Date();
             const isLiked = post.likes.includes(currentUser.id);
             return (
                 <div key={post.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden animate-slide-up">
                     
                     {/* HEADER */}
                     <div className="p-4 flex justify-between items-start">
                         <div className="flex gap-3 items-center cursor-pointer" onClick={() => onLocateInTree(author.id)}>
                             <img src={author.avatarUrl} className="w-10 h-10 rounded-full bg-gray-100 border border-gray-100" />
                             <div>
                                 <h3 className="font-bold text-sm text-gray-900 flex items-center gap-2">
                                     {author.name}
                                     {post.type === 'event' && <span className="bg-indigo-100 text-indigo-700 text-[9px] px-2 py-0.5 rounded-full font-black uppercase tracking-wider">Invite</span>}
                                     {post.privacy === 'specific' && <Users className="w-3 h-3 text-purple-500" />}
                                 </h3>
                                 <div className="flex items-center gap-1 text-xs text-gray-400 mt-0.5">
                                    <span>{timeAgo(post.timestamp)}</span>
                                    <span>•</span>
                                    <Globe className="w-3 h-3" />
                                 </div>
                             </div>
                         </div>
                         <div className="relative">
                            <button onClick={() => setActiveMenuId(activeMenuId === post.id ? null : post.id)} className="p-1.5 hover:bg-gray-50 rounded-full transition-colors"><MoreHorizontal className="w-5 h-5 text-gray-400" /></button>
                            {activeMenuId === post.id && (
                                <div className="absolute right-0 top-8 bg-white shadow-xl border border-gray-100 rounded-xl overflow-hidden w-32 z-10 animate-scale-in origin-top-right">
                                    {currentUser.id === post.authorId ? (
                                        <>
                                            <button onClick={() => setEditingPost(post)} className="w-full text-left px-4 py-2.5 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2"><Edit2 className="w-3 h-3"/> Edit</button>
                                            <button onClick={() => handleDeletePost(post.id)} className="w-full text-left px-4 py-2.5 text-xs text-red-600 hover:bg-red-50 flex items-center gap-2"><Trash2 className="w-3 h-3"/> Delete</button>
                                        </>
                                    ) : (
                                        <button onClick={() => handleReport(post)} className="w-full text-left px-4 py-2.5 text-xs text-orange-600 hover:bg-orange-50 flex items-center gap-2"><Flag className="w-3 h-3"/> Report</button>
                                    )}
                                </div>
                            )}
                         </div>
                     </div>

                     {/* CONTENT */}
                     <div className="px-4 pb-4">
                         {isLocked ? (
                             <div className="bg-amber-50 p-8 rounded-xl border border-amber-100 text-center flex flex-col items-center gap-2">
                                 <div className="bg-amber-100 p-3 rounded-full"><Lock className="w-6 h-6 text-amber-600" /></div>
                                 <p className="text-amber-800 font-bold text-sm">Locked Memory</p>
                                 {/* @ts-ignore */}
                                 <p className="text-amber-600 text-xs">Unlocks on {new Date(post.unlockDate).toLocaleDateString()}</p>
                             </div>
                         ) : (
                             <>
                                {post.type === 'event' && post.eventDetails && (
                                    <div className="mb-4 bg-gray-50 p-4 rounded-xl border border-gray-200">
                                        <div className="flex flex-wrap gap-3 mb-3 items-center">
                                            <div className="bg-indigo-100 text-indigo-700 rounded-lg p-2 flex flex-col items-center justify-center min-w-[50px]">
                                                {/* @ts-ignore */}
                                                <span className="text-xs font-bold uppercase">{new Date(post.eventDetails.date).toLocaleString('default', { month: 'short' })}</span>
                                                {/* @ts-ignore */}
                                                <span className="text-xl font-black">{new Date(post.eventDetails.date).getDate()}</span>
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <h2 className="text-base font-black text-gray-900 leading-tight break-words">{post.eventDetails.title}</h2>
                                                <div className="flex flex-wrap items-center gap-1 text-xs font-bold text-gray-500 mt-1">
                                                    <MapPin className="w-3 h-3" /> <span className="break-words">{post.eventDetails.location}</span>
                                                </div>
                                            </div>
                                        </div>
                                        {/* RSVP BUTTONS */}
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 w-full">
                                            {['attending', 'maybe', 'declined'].map(status => {
                                                // @ts-ignore
                                                const isActive = post.rsvp?.[status]?.includes(currentUser.id);
                                                // @ts-ignore
                                                const count = post.rsvp?.[status]?.length || 0;
                                                return (
                                                    <button 
                                                        key={status}
                                                        onClick={() => handleRSVP(post.id, status as any)}
                                                        className={`py-2 rounded-lg text-xs font-bold capitalize transition-all flex flex-col items-center justify-center w-full ${isActive ? 'bg-indigo-600 text-white shadow-md transform scale-105' : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-100'}`}
                                                    >
                                                        <span>{status}</span>
                                                        <span className={`text-[9px] ${isActive ? 'text-indigo-200' : 'text-gray-400'}`}>{count}</span>
                                                    </button>
                                                )
                                            })}
                                        </div>
                                    </div>
                                )}

                                <p className="text-gray-800 text-sm mb-3 whitespace-pre-wrap leading-relaxed">{post.content}</p>
                                
                                {post.imageUrl && (
                                    <div className="rounded-xl overflow-hidden bg-black shadow-sm cursor-pointer group" onClick={() => setLightboxImage({ src: post.imageUrl!, type: post.mediaType || 'image' })}>
                                        {post.mediaType === 'video' ? (
                                            <div className="relative">
                                                <video src={post.imageUrl} className="w-full max-h-[500px] object-cover opacity-90 group-hover:opacity-100 transition-opacity" />
                                                <div className="absolute inset-0 flex items-center justify-center"><div className="bg-black/50 p-4 rounded-full backdrop-blur-sm"><Play className="w-8 h-8 text-white fill-white" /></div></div>
                                            </div>
                                        ) : (
                                            <img src={post.imageUrl} className="w-full max-h-[500px] object-cover" />
                                        )}
                                    </div>
                                )}
                             </>
                         )}
                     </div>

                     {/* FOOTER ACTIONS */}
                     <div className="px-4 py-3 border-t border-gray-50 flex gap-1 justify-between">
                         <button onClick={() => handleLocalLike(post.id)} className={`flex-1 flex items-center justify-center gap-2 py-2 text-xs font-bold rounded-lg transition-colors ${isLiked ? 'text-pink-600 bg-pink-50' : 'text-gray-500 hover:bg-gray-50 hover:text-pink-600'}`}>
                         <Heart className={`w-4 h-4 ${isLiked ? 'fill-pink-600' : ''}`} /> {isLiked ? 'Liked' : 'Like'}
                         </button>
                         {post.likes.length > 0 && (
                             <button onClick={() => setViewingLikesPostId(post.id)} className="px-3 py-2 text-xs font-bold text-pink-600 hover:bg-pink-50 rounded-lg transition-colors cursor-pointer">
                                 {post.likes.length} {post.likes.length === 1 ? 'Like' : 'Likes'}
                             </button>
                         )}
                         <button onClick={() => setActiveCommentId(activeCommentId === post.id ? null : post.id)} className={`flex-1 flex items-center justify-center gap-2 py-2 text-xs font-bold rounded-lg transition-colors ${activeCommentId === post.id ? 'bg-indigo-50 text-indigo-600' : 'text-gray-500 hover:bg-gray-50 hover:text-indigo-600'}`}><MessageCircle className="w-4 h-4" /> Comment</button>
                         <button onClick={() => handleRemind(post.id)} className="flex-1 flex items-center justify-center gap-2 py-2 text-xs font-bold text-gray-500 hover:bg-gray-50 rounded-lg transition-colors hover:text-green-600"><RefreshCw className="w-4 h-4" /> Remind</button>
                     </div>

                     {/* COMMENTS AREA */}
                     {activeCommentId === post.id && (
                         <div className="bg-gray-50/50 p-4 border-t border-gray-100 animate-slide-down">
                             {/* Recursive List */}
                             <div className="space-y-4 mb-4 max-h-60 overflow-y-auto custom-scrollbar pr-2">
                                 {/* @ts-ignore */}
                                 {post.commentsList?.length > 0 ? (
                                     // @ts-ignore
                                     post.commentsList.map(c => (
                                         <CommentItem key={c.id} comment={c} members={members} onReply={(id, name) => setReplyTarget({id, name})} />
                                     ))
                                 ) : (
                                     <div className="text-center py-6 text-gray-400">
                                         <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-20" />
                                         <p className="text-xs">No comments yet.</p>
                                     </div>
                                 )}
                             </div>
                             
                             {/* Input */}
                             <div className="flex flex-col gap-2">
                                 {replyTarget && (
                                     <div className="flex justify-between items-center text-[10px] text-white bg-indigo-500 px-3 py-1 rounded-lg shadow-sm">
                                         <span>Replying to <b>{replyTarget.name}</b></span>
                                         <button onClick={() => setReplyTarget(null)}><X className="w-3 h-3" /></button>
                                     </div>
                                 )}
                                 <div className="flex gap-2">
                                     <input 
                                        className="flex-1 bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-xs outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-50 transition-all shadow-sm"
                                        placeholder={replyTarget ? "Write a reply..." : "Write a comment..."} 
                                        value={commentText} onChange={e => setCommentText(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && handleComment(post.id)}
                                     />
                                     <button onClick={() => handleComment(post.id)} disabled={!commentText.trim()} className="p-2.5 bg-indigo-600 text-white rounded-xl shadow-md hover:bg-indigo-700 disabled:opacity-50 transition-all"><Send className="w-4 h-4" /></button>
                                 </div>
                             </div>
                         </div>
                     )}
                 </div>
             );
         })}
      </div>
    </div>
  );
};

export default SocialFeed;